import { SCHEMA } from '@vedalib/editor/lib/brand'
import { useCallback, useMemo } from 'react'
import React from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import {
  FieldColorPicker,
  FieldNumber,
  FieldNumberArray,
  FieldSegmented,
  FieldSlider,
  FormField,
} from 'components/formKit'
import { TileButton } from 'components/uiKit/Button'
import { Typography } from 'components/uiKit/Typography'
import { BrandFieldShadow, BrandTagEdited } from 'services/Branding/components'
import { BORDER_OPTIONS, OFFSET_ARRAY } from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { isAnyEdited } from 'services/Branding/fieldConfigs/utils'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useUpdateSchema } from 'services/Branding/hooks'
import { BrandFormEnum } from 'services/Branding/types'
import { t } from 'services/Translation'

import type { CheckboxFormBrandType } from './checkboxTypes'

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.checkbox.style, {
  item: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.checkbox.schema.color.label'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  itemDisable: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.checkbox.schema.disabled.color.label'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  itemHover: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.hoverColor'),
      props: { transparent: true, labeled: true, resettable: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  checkbox: {
    width: genBrandField({
      as: FieldNumber,
      label: t('elements.checkbox.schema.width.label'),
      rules: [{ min: 1, message: ' ' }],
      props: { width: 64 },
      useConfigs: [useThemeValueGetter],
    }),
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      useConfigs: [useBrandColorOptions],
    }),
  },
  checkboxSelected: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.checkbox.schema.checkboxSelected.label'),
      useConfigs: [useBrandColorOptions],
    }),
  },
  iconSelected: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.checkbox.schema.iconSelected.label'),
      useConfigs: [useBrandColorOptions],
    }),
  },
  checkboxDisabled: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      useConfigs: [useBrandColorOptions],
    }),
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.checkbox.schema.checkboxDisabled.color.label'),
      useConfigs: [useBrandColorOptions],
    }),
  },
  checkboxBorder: {
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.checkbox.schema.checkboxBorder.borderColor.label'),
      useConfigs: [useBrandColorOptions],
    }),
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.checkbox.schema.checkboxBorder.disabled.color.label'),
      useConfigs: [useBrandColorOptions],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  border: {
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.checkbox.schema.effects.boxShadow.label'),
      useConfigs: [useThemeValueGetter],
    }),
  },
  indents: {
    padding: genBrandField({
      as: FieldNumberArray,
      label: t('elements.checkbox.schema.effects.padding.label'),
      layout: 'vertical',
      props: { options: OFFSET_ARRAY },
    }),
    gap: genBrandField({
      as: FieldNumber,
      label: t('elements.checkbox.schema.effects.gap.label'),
      props: { width: 64 },
    }),
  },
})

const CheckboxFormStyle: CheckboxFormBrandType = ({ device, subConfigs, setForm, edited }) => {
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)
  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom gap sides>
        <FormField config={schema.checkbox.width} />
        <Typography weight='medium'>
          {t('elements.checkbox.schema.title.checkboxActive')}
        </Typography>
        <FormField config={schema.checkbox.backgroundColor} />
        <FormField config={schema.checkboxSelected.backgroundColor} />
        <FormField config={schema.iconSelected.backgroundColor} />
        <Typography weight='medium'>
          {t('elements.checkbox.schema.title.checkboxDisabled')}
        </Typography>
        <FormField config={schema.checkboxDisabled.backgroundColor} />
        <FormField config={schema.checkboxDisabled.color} />
        <Typography weight='medium'>
          {t('elements.checkbox.schema.title.checkboxBorder')}
        </Typography>
        <FormField config={schema.checkboxBorder.borderRadius} />
        <FormField config={schema.checkboxBorder.borderStyle} />
        <FormField config={schema.checkboxBorder.borderColor} />
        <FormField config={schema.checkboxBorder.color} />
        <FormField config={schema.checkboxBorder.borderWidth} />
        <Typography weight='medium'>{t('elements.checkbox.schema.title.Row')}</Typography>
        <FormField config={schema.item.backgroundColor} />
        <FormField config={schema.itemDisable.backgroundColor} />
        <FormField config={schema.itemHover.backgroundColor} />
        <FormField config={schema.item.borderRadius} />
        <FormField config={schema.item.borderStyle} />
        <FormField config={schema.item.borderColor} />
        <FormField config={schema.item.borderWidth} />
        <Typography weight='medium'>{t('style.effects')}</Typography>
        <FormField config={schema.effects.boxShadow} />
        <FormField config={schema.indents.padding} />
        <FormField config={schema.indents.gap} />
        <TileButton
          description={t('uiKit.button.richTextDescription')}
          icon='otherRight'
          name='showFontForm'
          onClick={handleSetTypography}
        >
          <Typography weight='medium'>{t('page.brand.tab.typography')}</Typography>
          {isAnyEdited(edited.font) && <BrandTagEdited />}
        </TileButton>
      </Padding>
    </LayoutScroll>
  )
}

export default React.memo(CheckboxFormStyle) as typeof CheckboxFormStyle
